import Sidebar, { MobileSidebar, useMobileSidebar } from "@/components/Sidebar";
import { Outlet, Link } from "react-router-dom";
import { AlertTriangle, Sparkles, MailCheck, Menu } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useMediaQuery } from "@/lib/useMediaQuery";

function VerifyEmailBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  if (!user || user === false) return null;
  if (user.email_verified || dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(data.already_verified ? "Already verified — refresh the page." : "Verification email sent. Check your inbox.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send verification email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="verify-email-banner"
      className="bg-[#fff7ec] border-b border-[#f1e0c0] text-[#7a3d12] text-sm px-6 py-2.5 flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-2 min-w-0">
        <MailCheck className="w-4 h-4 shrink-0" strokeWidth={1.8} />
        <span className="truncate">
          Please verify <b className="font-mono text-xs">{user.email}</b> — check your inbox for the link.
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={resend}
          disabled={sending}
          data-testid="verify-email-resend"
          className="text-xs uppercase tracking-[0.2em] bg-[#1b3b36] text-white px-3 py-1.5 rounded-[3px] hover:bg-[#2c5a53] transition-colors disabled:opacity-60"
        >
          {sending ? "Sending…" : "Resend"}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="verify-email-dismiss"
          className="text-xs underline text-[#7a3d12]/80 hover:text-[#7a3d12]"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function SubscriptionBanner() {
  const { user } = useAuth();
  if (!user || user === false) return null;
  // Complimentary lifetime accounts never show subscription banners.
  if (user.complimentary === true) return null;
  // Read-only (subscription expired/canceled)
  if (user.write_access === false) {
    return (
      <div
        data-testid="readonly-banner"
        className="bg-[#fbeae8] border-b border-[#f1c5be] text-[#a23a30] text-sm px-6 py-2.5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={1.8} />
          <span className="truncate">
            Workspace is read-only. Subscribe to restore creating, editing and exports.
          </span>
        </div>
        {user.role === "owner" && (
          <Link
            to="/settings/billing"
            data-testid="readonly-banner-cta"
            className="text-xs uppercase tracking-[0.2em] bg-[#a23a30] text-white px-3 py-1.5 rounded-[3px] hover:bg-[#8a2f27] transition-colors shrink-0"
          >
            Subscribe
          </Link>
        )}
      </div>
    );
  }
  // Trial nearing expiry warning
  if (
    user.subscription_status === "trialing" &&
    user.trial_ends_at &&
    user.role === "owner"
  ) {
    const ms = new Date(user.trial_ends_at).getTime() - Date.now();
    const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
    if (days > 5) return null;
    return (
      <div
        data-testid="trial-banner"
        className="bg-[#fbeae2] border-b border-[#e9c4a3] text-[#7a3d12] text-sm px-6 py-2.5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 shrink-0" strokeWidth={1.8} />
          <span className="truncate">
            {days === 0
              ? "Your trial ends today."
              : `Your free trial ends in ${days} day${days === 1 ? "" : "s"}.`}
          </span>
        </div>
        <Link
          to="/settings/billing"
          data-testid="trial-banner-cta"
          className="text-xs uppercase tracking-[0.2em] bg-[#1b3b36] text-white px-3 py-1.5 rounded-[3px] hover:bg-[#2c5a53] transition-colors shrink-0"
        >
          Add plan
        </Link>
      </div>
    );
  }
  return null;
}

function MobileTopbar({ onMenu }) {
  const { user } = useAuth();
  return (
    <div className="flex items-center gap-3 bg-white border-b border-[#e5e2d9] px-4 h-14" data-testid="mobile-topbar">
      <button
        type="button"
        onClick={onMenu}
        data-testid="mobile-menu-button"
        aria-label="Open navigation"
        className="p-2 -ml-2 text-[#1b3b36] hover:bg-[#efece6] rounded-[3px] transition-colors"
      >
        <Menu className="w-5 h-5" strokeWidth={1.8} />
      </button>
      <div className="font-display text-lg font-medium tracking-tight">
        Hearth<span className="text-[#c0612f]">.</span>
      </div>
      {user?.business_name && (
        <div className="ml-auto text-[10px] uppercase tracking-[0.18em] text-[#8a8476] truncate max-w-[120px]">
          {user.business_name}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { open, setOpen } = useMobileSidebar();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  return (
    <div className="min-h-screen flex bg-[#f7f5f0] text-[#1c1917]">
      {isDesktop && <Sidebar />}
      {!isDesktop && <MobileSidebar open={open} onOpenChange={setOpen} />}
      <main className="flex-1 min-w-0 flex flex-col">
        {!isDesktop && <MobileTopbar onMenu={() => setOpen(true)} />}
        <VerifyEmailBanner />
        <SubscriptionBanner />
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
